import type { FC, JSX, PropsWithChildren } from "react";
import React, { useImperativeHandle, useMemo, useRef } from "react";

import cn from "classnames";

import {
    DEFAULT_TAG_SIZE,
    TAG_SIZE_TO_ASSET_SIZE,
    TAG_SIZE_TO_COUNTER_SIZE,
} from "./constants/sizes";
import { Box, boxForwardRef } from "../../../helpers/common/Box/Box";
import { ButtonDiv } from "../../../helpers/common/ButtonDiv";
import { getChildrenRecord } from "../../../utils/react";

import type { TagSizes, TagLeftAddons, TagStates } from "./types";
import type { BoxComponentWithDefault } from "../../../helpers/common/Box/Box";
import type { Optional, PropsWithBase } from "../../../types/react";

import classes from "./Tag.styl";

export const TAG_DISPLAY_NAME = "Tag";
const TAG_TEST_ID = "tag-test-id";

export interface ITag extends PropsWithChildren {
    /** Отвечает за исходный размер тэга и значения, которые идут в комбинации с этим размером. Набор значений действителен для размера шрифта/иконок в состоянии Default */
    size?: TagSizes;
    /** Отвечает за выбор элемента, который будет отображен слева от текста */
    leftAddon?: TagLeftAddons;
    /** Отвечает за наличие обводки */
    stroke?: boolean;
    /** Отвечает за состояние компонента, основанное на комбинации включенных/выключенных параметров */
    state?: TagStates;
}

export type TagProps = Optional<PropsWithBase<ITag>, "data-tsid">;

/**
 * Базовый компонент для присваивания статуса или свойства контенту в виде лейбла.
 *
 * В качестве контента передаётся Label, Icon, Counter, ButtonInline. Могут отображаться, а могут быть скрыты.
 * В минимальной комплектации Tag содержит Label.
 *
 * В качестве `as` ожидаем, что будет передан 'ButtonDiv' или 'a'.
 * Более подробную информацию можно найти [тут](/docs/документация-разработчикам-дс-компоненты-инструкция-по-разработке-полиморфных-компонентов--docs).
 *
 * <a href="https://www.figma.com/design/vTg3KAvmAXA9LigeyiqCWL/%F0%9F%92%A0-Base-Components?m=auto&node-id=2-85901&t=xOS5YJTI37Iwp4AM-1" target="_blank">Спека</a>
 * @constructor
 */
export const Tag = boxForwardRef<BoxComponentWithDefault<"button", TagProps>>(
    (
        {
            as = ButtonDiv,
            state = "none",
            view = "primary",
            size = DEFAULT_TAG_SIZE,
            leftAddon,
            stroke = false,
            "data-tsid": tsid,
            customColorsStyles,
            children,
            ...restProps
        },
        ref,
    ): JSX.Element => {
        const tagRef = useRef<HTMLDivElement | null>(null);
        useImperativeHandle(ref, () => tagRef.current);

        const tagClasses = cn(
            classes.tag,
            state !== "none" && classes[`__state-${state}`],
            {
                [classes[`__style-${view}`]]: view,
                [classes[`__size-${size}`]]: size,
                [classes["__stroke"]]: stroke,
            },
            customColorsStyles,
        );

        if (state === "none") {
            return (
                <div
                    {...restProps}
                    className={tagClasses}
                    data-tsid={tsid || TAG_TEST_ID}
                    data-uikit={TAG_DISPLAY_NAME}
                >
                    <TagContent size={size} leftAddon={leftAddon}>
                        {children}
                    </TagContent>
                </div>
            );
        }

        return (
            <Box
                {...restProps}
                as={as}
                data-tsid={tsid || TAG_TEST_ID}
                data-uikit={TAG_DISPLAY_NAME}
                className={tagClasses}
                ref={tagRef}
            >
                <TagContent size={size} leftAddon={leftAddon}>
                    {children}
                </TagContent>
            </Box>
        );
    },
);

Tag.displayName = TAG_DISPLAY_NAME;

const TAG_CONTENT_TEST_ID = "tag-content-test-id";

interface ITagContent extends Pick<ITag, "size" | "leftAddon" | "children"> {}

const TagContent: FC<ITagContent> = (props): JSX.Element => {
    const { size = DEFAULT_TAG_SIZE, leftAddon, children } = props;

    const { LeftAddon, Label, ButtonInline } = useMemo(() => {
        return getChildrenRecord(children, TAG_DISPLAY_NAME, {
            Label: {
                required: true,
                props: { size },
            },
            LeftAddon: {
                props: {
                    size:
                        leftAddon === "counter"
                            ? TAG_SIZE_TO_COUNTER_SIZE[size]
                            : TAG_SIZE_TO_ASSET_SIZE[size],
                    tagSize: size,
                    leftAddon,
                },
            },
            ButtonInline: {
                props: {
                    size: TAG_SIZE_TO_ASSET_SIZE[size],
                },
            },
            Unknown: {
                skipElement: false,
            },
        });
    }, [size, children, leftAddon]);

    return (
        // eslint-disable-next-line ok-custom-rules/check-attribute
        <div className={classes.content} data-tsid={TAG_CONTENT_TEST_ID}>
            {LeftAddon}
            {Label}
            {ButtonInline}
        </div>
    );
};

export default Tag;
